import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { EnhancedNoteMarker } from './GuitarNeckVisualizerComponents';
import { getProperNoteDisplay, resolveAllNotesIndex, getIntervalDegreeLabel } from '../utils/musicTheory';
import { getNoteClassNames } from '../styles/NoteStyles';

// Coach: slow synchronized pulse on the squares (the chord tones to target).
// Neutral white glow so it reads on both the green root and the violet squares.
const coachSquarePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); transform: scale(1); }
  50% { box-shadow: 0 0 16px 4px rgba(255,255,255,0.6); transform: scale(1.12); }
`;

const TouchableNoteMarker = ({
  stringIndex,
  actualFret,
  noteValue,
  activeScale,
  selectedNotes,
  highlightArpeggios,
  selectedArpeggio,
  getArpeggioDegree,
  isRootNote,
  isTonicOutline = false,
  isModeColor,
  noteIsInPattern,
  isInScale,
  isArpeggio,
  isChordTone,
  highlightChordNotes,
  isChordHighlightNote,
  getChordDegree,
  currentColor,
  visibleColors,
  handleNoteClick,
  handleNoteInteraction,
  isPaintMode = false,
  noteBorder,
  textColor,
  noteOpacity,
  noteDisplayMode = 'none',
  markerDots = 0,
  showWholeScale = false,
  isEarLit = false,
  coachPulse = false,
  lessonMode = false,
  lessonNote = null,
  compact = false
}) => {
  // iOS touch fix: Global tap debouncing to prevent rapid-fire touch events
  if (!window.lastGlobalTap) {
    window.lastGlobalTap = 0;
  }

  // Brief scale-up when the note is tapped to play it (paint mode off)
  const [flashing, setFlashing] = useState(false);
  const flashTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  // Act immediately on press (pointer down), not on release: the note should
  // sound the instant it's touched, with no long-press wait.
  const handlePress = () => {
    const now = Date.now();
    if (now - window.lastGlobalTap < 60) {
      return; // Block rapid-fire duplicate events (iOS Safari)
    }
    window.lastGlobalTap = now;

    if (!isPaintMode) {
      setFlashing(true);
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashing(false), 280);
    }

    if (handleNoteClick) {
      handleNoteClick(stringIndex, actualFret, noteValue);
    }
  };

  // Find if this note is selected
  const isSelectedNote = selectedNotes.find(n => n.stringIndex === stringIndex && n.fret === actualFret);

  // Mode lecon : le manche ne montre plus que la section et le pas courant. Les
  // notes de section gardent le mauve de la gamme, la note du pas s'allume en
  // mauve clair. Racine verte, ancre, arpeges, couleurs de peinture et carres
  // d'accord sont neutralises le temps de la lecon.
  const isLessonCurrent = lessonMode && !!lessonNote && !!lessonNote.current;

  // Compute scale degree (1-based) for this note
  const scaleDegree = (() => {
    if (!activeScale || !activeScale.notes) return null;
    const displayNote = getProperNoteDisplay(noteValue, activeScale);
    const idx = activeScale.notes.indexOf(displayNote);
    return idx !== -1 ? idx + 1 : null;
  })();

  // Degre de gamme tel que le mode Vue "degrees" l'affiche : que des chiffres
  // avec leur alteration (1, b3, #4, b7...), jamais de nom de note, y compris
  // pour la premiere note de la gamme et pour les notes hors gamme.
  const degreeLabel = () => getIntervalDegreeLabel(noteValue, activeScale);

  // Compute the display label based on noteDisplayMode.
  // Une note du pas courant d'une lecon porte toujours une etiquette : le degre
  // d'accord envoye par le lecteur, sinon le degre de gamme, quel que soit le
  // reglage de Vue. Les notes de section suivent le reglage comme les autres.
  const getDisplayLabel = () => {
    if (isLessonCurrent) return lessonNote.label || degreeLabel();
    // Case frette 0 (label de corde) : la lettre de la corde reste lisible meme
    // quand Vue n'affiche rien, c'est elle qui identifie la corde.
    if (noteDisplayMode === 'none') return compact ? getProperNoteDisplay(noteValue, activeScale) : '';
    const displayNote = getProperNoteDisplay(noteValue, activeScale);
    if (noteDisplayMode === 'names') {
      return displayNote;
    }
    // Degree mode: numbers with accidentals only
    return degreeLabel();
  };

  // "Strong" notes (target notes) = the pitch classes of the chord that's sounding,
  // so the hierarchy follows the drone/triad/7th voicing instead of fixed scale
  // degrees: the 7th reads as passage over a triad and as a target over a 7th chord.
  // The scale root stays the green anchor on its own (isRootNote).
  const notePitchClass = resolveAllNotesIndex(noteValue);
  const isVoicingTone = Array.isArray(activeScale?.voicingTonePcs)
    && activeScale.voicingTonePcs.includes(notePitchClass);

  // Square shape = a voicing tone that isn't the root (the root is already squared
  // by its own style). Scoped to the whole-scale view; Shapes mode keeps the old
  // fixed-degree behavior.
  const isChordToneShape = showWholeScale
    ? (isVoicingTone && !isRootNote)
    : (scaleDegree === 3 || scaleDegree === 5 || scaleDegree === 7);

  // When Shapes is OFF (showWholeScale): strong = voicing tones + root.
  // When Shapes is ON: use pattern as-is (no override).
  const isStrongNote = showWholeScale
    ? (isVoicingTone || isRootNote)
    : (scaleDegree === 1 || scaleDegree === 3 || scaleDegree === 5 || scaleDegree === 7);
  const effectiveNoteIsInPattern = showWholeScale ? isStrongNote : noteIsInPattern;

  // Etats neutralises par la lecon. Hors lecon ils valent exactement les etats
  // d'origine, le DOM du mode Play ne bouge donc pas.
  const effRoot = lessonMode ? false : isRootNote;
  const effTonicOutline = lessonMode ? false : isTonicOutline;
  const effModeColor = lessonMode ? false : isModeColor;
  const effChordToneShape = lessonMode ? isLessonCurrent : isChordToneShape;
  const effSelectedNote = lessonMode ? undefined : isSelectedNote;
  // En lecon, seules les notes de la section et du pas sont rendues, avec les
  // etats du manche de gamme : section = niveau 3 (rond au contour fin,
  // hl_scale-note-offscale), pas courant = niveau 2 (carre cible au contour
  // epais). Aucun style propre a la lecon.
  const effNoteIsInPattern = lessonMode ? isLessonCurrent : effectiveNoteIsInPattern;

  // Lab : la frette reperee pose ses points nacres dans le rond. Rond vide, ils
  // prennent le centre ; quand Vue ecrit un nom ou un degre, ils glissent sous
  // le label au lieu de disparaitre, le repere de position ne depend pas du
  // reglage d'etiquettes.
  const displayLabel = getDisplayLabel();
  const showMarkerDots = markerDots > 0;
  const dotsUnderLabel = showMarkerDots && !!displayLabel;

  return (
    <EnhancedNoteMarker
      className={`
        string-row-note-marker
        ${compact ? 'string-row-note-marker-compact' : ''}
        ${effRoot ? 'string-row-note-root' : ''}
        ${effTonicOutline ? 'string-row-note-tonic-outline' : ''}
        ${effChordToneShape ? 'chord-tone-square' : ''}
        ${effModeColor ? 'string-row-note-mode-color' : ''}
        ${effNoteIsInPattern ? 'string-row-note-pattern' : ''}
        ${effSelectedNote ? 'string-row-note-playing' : ''}
        ${getNoteClassNames({
          noteValue,
          isRoot: effRoot,
          isModeColorNote: effModeColor,
          noteIsInPattern: effNoteIsInPattern,
          isInScale,
          isSelected: !!effSelectedNote,
          isArpeggio: lessonMode ? false : (selectedArpeggio ? selectedArpeggio.notes.includes(noteValue) : isArpeggio),
          isChordTone: lessonMode ? false : isChordTone,
          isChordHighlightNote: lessonMode ? false : isChordHighlightNote,
          arpeggioDegree: getArpeggioDegree ? getArpeggioDegree(stringIndex, actualFret, activeScale, selectedArpeggio) : '',
          selectedColor: effSelectedNote?.color || currentColor,
          visibleColors,
          stringNumber: stringIndex + 1, // Convert 0-based index to 1-based string number
          fretNumber: actualFret
        })}
      `}
      isroot={effRoot}
      ismodecolor={effModeColor}
      ispattern={effNoteIsInPattern}
      isplaying={!!effSelectedNote}
      sx={{
        backgroundColor: '#000000',
        border: noteBorder,
        opacity: noteOpacity,
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        zIndex: (flashing || isEarLit || (coachPulse && isStrongNote)) ? 5 : 3,
        ...(coachPulse && isStrongNote && !flashing && !isEarLit && {
          animation: `${coachSquarePulse} 1.6s ease-in-out infinite`
        }),
        ...(flashing && { transform: 'scale(1.22)' }),
        ...(isEarLit && {
          transform: 'scale(1.3)',
          boxShadow: '0 0 12px 4px rgba(255, 255, 255, 0.85)'
        })
      }}
      onPointerDown={handlePress}
    >
      <Typography 
        className="string-row-note-text"
        variant="caption" 
        sx={{ fontWeight: 'bold', color: compact ? '#fff' : textColor, opacity: (compact || (lessonMode ? isLessonCurrent : isStrongNote)) ? 1 : 0.5 }}
      >
        {displayLabel}
        {highlightArpeggios && (
          <span 
            className="string-row-note-arpeggio-degree-label"
            style={{ 
              fontSize: '0.6rem', 
              position: 'absolute', 
              bottom: -8, 
              left: '50%', 
              transform: 'translateX(-50%)',
              backgroundColor: 'black',
              border: '2px black',
              padding: '0px 1px 0px 1px'
            }}
          >
            {(() => {
              if (selectedArpeggio && selectedArpeggio.notesWithDegrees && Array.isArray(selectedArpeggio.notesWithDegrees)) {
                const match = selectedArpeggio.notesWithDegrees.find(item => {
                  const normalizedArpeggioNote = item.note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
                  const normalizedCurrentNote = noteValue.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
                  return normalizedArpeggioNote === normalizedCurrentNote;
                });
                if (match) return match.scaleDegree;
              }

              if (isArpeggio && getArpeggioDegree && typeof getArpeggioDegree === 'function') {
                return getArpeggioDegree(stringIndex, actualFret, activeScale, selectedArpeggio);
              }
              return '';
            })()}
          </span>
        )}
        {highlightChordNotes && isChordHighlightNote && (
          <span className="string-row-note-chord-degree-label">
            {getChordDegree && typeof getChordDegree === 'function' 
              ? getChordDegree(stringIndex, actualFret, activeScale) 
              : ''}
          </span>
        )}
      </Typography>
      {/* Hors du Typography : les points parlent de la frette, pas de la note,
          ils ne suivent donc pas l'attenuation du texte. Sous le label, ils
          descendent dans les pixels libres en bas du rond et perdent un pixel,
          juste assez pour se lire sans concurrencer le chiffre. */}
      {showMarkerDots && (
        <Box
          className="string-row-note-marker-dots"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: dotsUnderLabel ? '2px' : '3px',
            pointerEvents: 'none',
            ...(dotsUnderLabel ? { bottom: '4px' } : { top: 0, bottom: 0 })
          }}
        >
          {Array.from({ length: markerDots }, (_, i) => (
            <Box
              key={`marker-dot-${i}`}
              className="string-row-note-marker-dot"
              sx={{
                width: dotsUnderLabel ? '3px' : '4px',
                height: dotsUnderLabel ? '3px' : '4px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.45)',
                flex: 'none'
              }}
            />
          ))}
        </Box>
      )}
    </EnhancedNoteMarker>
  );
};

export default React.memo(TouchableNoteMarker);